import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Plus, Trash2, Save, Edit3, Eye, EyeOff, Loader2, Link as LinkIcon, ArrowLeft, X } from "lucide-react";
import { Link } from "react-router-dom";

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

const emptyDraft = () => ({
  slug: "",
  title: "",
  description: "",
  date: new Date().toISOString().slice(0, 10),
  read_minutes: 6,
  keywords: [],
  body: [{ type: "p", text: "" }],
  published: false,
});

/**
 * Admin blog manager: list, create, edit, delete, AI-generate.
 * Lives inside the Admin page as the "Blog" tab.
 */
export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // null | "new" | <existing post object>
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/blog");
      setPosts(data.posts || []);
    } catch (e) {
      toast.error("Failed to load blog posts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => { setDraft(emptyDraft()); setEditing("new"); };
  const startEdit = async (slug) => {
    try {
      const { data } = await api.get(`/admin/blog/${slug}`);
      setDraft({
        ...data,
        keywords: data.keywords || [],
        body: (data.body && data.body.length) ? data.body : [{ type: "p", text: "" }],
      });
      setEditing(data);
    } catch {
      toast.error("Could not load post");
    }
  };
  const cancelEdit = () => { setEditing(null); setDraft(emptyDraft()); };

  const save = async () => {
    if (!draft.slug) draft.slug = slugify(draft.title);
    if (!draft.title.trim() || !draft.description.trim() || !draft.body.length) {
      toast.error("Title, description, and at least one body block are required.");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/admin/blog", draft);
        toast.success("Post created");
      } else {
        const { slug, ...patch } = draft;
        await api.patch(`/admin/blog/${editing.slug}`, patch);
        toast.success("Post saved");
      }
      cancelEdit();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (post) => {
    try {
      await api.patch(`/admin/blog/${post.slug}`, { published: !post.published });
      toast.success(post.published ? "Unpublished" : "Published");
      load();
    } catch {
      toast.error("Failed to toggle publish state");
    }
  };

  const deletePost = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/blog/${post.slug}`);
      toast.success("Post deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  // ------------ Body block editor helpers ------------
  const updateBlock = (idx, patch) => {
    setDraft((d) => ({ ...d, body: d.body.map((b, i) => i === idx ? { ...b, ...patch } : b) }));
  };
  const addBlock = (type) => {
    const newBlock = type === "ul" ? { type: "ul", items: [""] } : { type, text: "" };
    setDraft((d) => ({ ...d, body: [...d.body, newBlock] }));
  };
  const removeBlock = (idx) => {
    setDraft((d) => ({ ...d, body: d.body.filter((_, i) => i !== idx) }));
  };
  const moveBlock = (idx, dir) => {
    setDraft((d) => {
      const next = [...d.body];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return d;
      [next[idx], next[j]] = [next[j], next[idx]];
      return { ...d, body: next };
    });
  };

  // ------------------ AI Generator modal ------------------
  const handleAiGenerated = (aiDraft) => {
    setDraft({
      ...emptyDraft(),
      slug: aiDraft.slug || slugify(aiDraft.title || ""),
      title: aiDraft.title || "",
      description: aiDraft.description || "",
      date: aiDraft.date || new Date().toISOString().slice(0, 10),
      read_minutes: aiDraft.read_minutes || 6,
      keywords: aiDraft.keywords || [],
      body: (aiDraft.body && aiDraft.body.length) ? aiDraft.body : [{ type: "p", text: "" }],
      published: false,
    });
    setEditing("new");
    setAiOpen(false);
    toast.success("AI draft loaded — review and publish when ready");
  };

  // ------------------- RENDER -------------------
  if (editing) return (
    <BlogEditor
      draft={draft}
      setDraft={setDraft}
      onCancel={cancelEdit}
      onSave={save}
      saving={saving}
      isNew={editing === "new"}
      updateBlock={updateBlock}
      addBlock={addBlock}
      removeBlock={removeBlock}
      moveBlock={moveBlock}
    />
  );

  return (
    <div data-testid="admin-blog-panel">
      <div className="surface-card p-5 mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="overline mb-1">Blog posts</div>
          <div className="text-sm text-white/60">{posts.length} total · {posts.filter(p => p.published).length} published</div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setAiOpen(true)} className="btn-secondary text-sm" data-testid="admin-blog-ai-generate">
            <Sparkles className="w-4 h-4" /> Generate with AI
          </button>
          <button onClick={startNew} className="btn-primary text-sm" data-testid="admin-blog-new">
            <Plus className="w-4 h-4" /> New post
          </button>
        </div>
      </div>

      {loading && <div className="text-white/50 text-sm font-mono">Loading…</div>}

      <div className="space-y-3" data-testid="admin-blog-list">
        {posts.map((p) => (
          <div key={p.slug} className="surface-card p-4 sm:p-5 flex flex-wrap gap-3 items-center justify-between" data-testid={`admin-blog-row-${p.slug}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold ${p.published ? "bg-cyan/10 text-cyan" : "bg-white/5 text-white/50"}`}>
                  {p.published ? "Published" : "Draft"}
                </span>
                <span className="text-xs text-white/40 font-mono">{p.date}</span>
                <span className="text-xs text-white/40">· {p.read_minutes} min</span>
              </div>
              <div className="font-heading font-bold text-base sm:text-lg truncate">{p.title}</div>
              <div className="text-xs text-white/50 truncate font-mono mt-0.5">/{p.slug}</div>
            </div>
            <div className="flex gap-2 shrink-0">
              <Link to={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer" className="btn-ghost text-xs" data-testid={`admin-blog-view-${p.slug}`}><LinkIcon className="w-3 h-3" /></Link>
              <button onClick={() => togglePublish(p)} className="btn-ghost text-xs" data-testid={`admin-blog-toggle-${p.slug}`}>
                {p.published ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              </button>
              <button onClick={() => startEdit(p.slug)} className="btn-ghost text-xs" data-testid={`admin-blog-edit-${p.slug}`}><Edit3 className="w-3 h-3" /></button>
              <button onClick={() => deletePost(p)} className="btn-ghost text-xs text-rose-400 hover:text-rose-300" data-testid={`admin-blog-delete-${p.slug}`}><Trash2 className="w-3 h-3" /></button>
            </div>
          </div>
        ))}
        {!loading && posts.length === 0 && (
          <div className="surface-card p-8 text-center text-white/60 text-sm">
            No posts yet. Click <strong>Generate with AI</strong> to draft your first post.
          </div>
        )}
      </div>

      {aiOpen && <AiGenerateModal onClose={() => setAiOpen(false)} onGenerated={handleAiGenerated} />}
    </div>
  );
}

/* ===================================================================== */
/* Editor view                                                            */
/* ===================================================================== */
function BlogEditor({ draft, setDraft, onCancel, onSave, saving, isNew, updateBlock, addBlock, removeBlock, moveBlock }) {
  const onChange = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));
  return (
    <div data-testid="admin-blog-editor">
      <div className="flex items-center justify-between mb-6">
        <button onClick={onCancel} className="btn-ghost text-sm" data-testid="admin-blog-editor-back">
          <ArrowLeft className="w-4 h-4" /> Back to posts
        </button>
        <div className="flex gap-2">
          <button onClick={onSave} disabled={saving} className="btn-primary text-sm" data-testid="admin-blog-editor-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isNew ? "Create post" : "Save changes"}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Metadata */}
        <div className="lg:col-span-1 space-y-4">
          <div className="surface-card p-5 space-y-3">
            <Field label="Title">
              <input type="text" value={draft.title} onChange={onChange("title")} onBlur={() => !draft.slug && setDraft((d) => ({ ...d, slug: slugify(d.title) }))} className="inp" data-testid="admin-blog-field-title" />
            </Field>
            <Field label="Slug (URL)">
              <div className="flex items-stretch">
                <span className="px-3 py-2.5 bg-white/[0.02] border border-r-0 border-white/10 text-white/40 text-xs font-mono">/blog/</span>
                <input type="text" value={draft.slug} onChange={(e) => setDraft((d) => ({ ...d, slug: slugify(e.target.value) }))} className="inp flex-1 rounded-l-none font-mono text-xs" data-testid="admin-blog-field-slug" />
              </div>
            </Field>
            <Field label="Meta description (140–160 chars)">
              <textarea value={draft.description} onChange={onChange("description")} rows={3} className="inp" data-testid="admin-blog-field-description" />
              <div className="text-[10px] text-white/40 mt-1 font-mono">{draft.description.length} chars</div>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date">
                <input type="date" value={draft.date} onChange={onChange("date")} className="inp" data-testid="admin-blog-field-date" />
              </Field>
              <Field label="Read time (min)">
                <input type="number" min="1" max="60" value={draft.read_minutes} onChange={(e) => setDraft((d) => ({ ...d, read_minutes: parseInt(e.target.value || "5", 10) }))} className="inp" data-testid="admin-blog-field-readtime" />
              </Field>
            </div>
            <Field label="Keywords (comma-separated)">
              <input type="text" value={(draft.keywords || []).join(", ")} onChange={(e) => setDraft((d) => ({ ...d, keywords: e.target.value.split(",").map((k) => k.trim()).filter(Boolean) }))} className="inp" data-testid="admin-blog-field-keywords" />
            </Field>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!draft.published} onChange={(e) => setDraft((d) => ({ ...d, published: e.target.checked }))} className="w-4 h-4 accent-klein" data-testid="admin-blog-field-published" />
              <span className="text-sm">Publish (visible on /blog)</span>
            </label>
          </div>
        </div>

        {/* Body editor */}
        <div className="lg:col-span-2 space-y-3" data-testid="admin-blog-body">
          {draft.body.map((b, idx) => (
            <div key={idx} className="surface-card p-4" data-testid={`admin-blog-block-${idx}`}>
              <div className="flex items-center justify-between mb-2 gap-2">
                <span className="text-[10px] uppercase tracking-wider font-bold text-cyan">{b.type === "p" ? "Paragraph" : b.type === "h2" ? "Heading 2" : "Bullet list"}</span>
                <div className="flex gap-1 text-xs">
                  <button type="button" onClick={() => moveBlock(idx, -1)} className="btn-ghost py-1 px-2" title="Move up">↑</button>
                  <button type="button" onClick={() => moveBlock(idx, 1)} className="btn-ghost py-1 px-2" title="Move down">↓</button>
                  <button type="button" onClick={() => removeBlock(idx)} className="btn-ghost py-1 px-2 text-rose-400 hover:text-rose-300" title="Remove block"><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
              {b.type === "p" && (
                <textarea value={b.text || ""} onChange={(e) => updateBlock(idx, { text: e.target.value })} rows={4} className="inp" placeholder="Paragraph text…" data-testid={`admin-blog-block-text-${idx}`} />
              )}
              {b.type === "h2" && (
                <input type="text" value={b.text || ""} onChange={(e) => updateBlock(idx, { text: e.target.value })} className="inp font-bold" placeholder="Section heading…" data-testid={`admin-blog-block-h2-${idx}`} />
              )}
              {b.type === "ul" && (
                <textarea value={(b.items || []).join("\n")} onChange={(e) => updateBlock(idx, { items: e.target.value.split("\n").map((l) => l).filter((l, i, arr) => i < arr.length - 1 || l.trim() !== "") })} rows={Math.max(3, (b.items || []).length + 1)} className="inp font-mono text-xs" placeholder="One bullet per line…" data-testid={`admin-blog-block-ul-${idx}`} />
              )}
            </div>
          ))}
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => addBlock("p")} className="btn-ghost text-xs" data-testid="admin-blog-add-p"><Plus className="w-3 h-3" /> Paragraph</button>
            <button type="button" onClick={() => addBlock("h2")} className="btn-ghost text-xs" data-testid="admin-blog-add-h2"><Plus className="w-3 h-3" /> Heading</button>
            <button type="button" onClick={() => addBlock("ul")} className="btn-ghost text-xs" data-testid="admin-blog-add-ul"><Plus className="w-3 h-3" /> Bullet list</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-wider font-bold text-white/60 mb-1.5">{label}</div>
      {children}
    </label>
  );
}

/* ===================================================================== */
/* AI Generate modal                                                      */
/* ===================================================================== */
function AiGenerateModal({ onClose, onGenerated }) {
  const [topic, setTopic] = useState("");
  const [angle, setAngle] = useState("");
  const [keywords, setKeywords] = useState("");
  const [wordTarget, setWordTarget] = useState(1100);
  const [generating, setGenerating] = useState(false);

  const generate = async () => {
    if (!topic.trim()) { toast.error("Enter a topic"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/blog/generate", {
        topic: topic.trim(),
        angle: angle.trim(),
        target_keywords: keywords.split(",").map((k) => k.trim()).filter(Boolean),
        word_target: wordTarget,
        tone: "expert-friendly",
      });
      onGenerated(data.draft);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI generation failed");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="admin-blog-ai-modal">
      <button type="button" onClick={onClose} className="absolute inset-0 bg-obsidian/80 backdrop-blur-sm" aria-label="Close" />
      <div className="relative w-full max-w-lg surface-card p-6 sm:p-8" data-testid="admin-blog-ai-modal-panel">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-klein" />
            <div className="font-heading font-black text-xl tracking-tighter">Generate with AI</div>
          </div>
          <button type="button" onClick={onClose} className="btn-ghost p-2"><X className="w-4 h-4" /></button>
        </div>
        <div className="text-sm text-white/60 mb-5">
          Claude Sonnet drafts a 1,000+ word post. You'll review and publish manually — never auto-publishes.
        </div>
        <div className="space-y-3">
          <Field label="Topic *">
            <input type="text" value={topic} onChange={(e) => setTopic(e.target.value)} className="inp" placeholder="e.g. How real estate agents can use Instagram DM automation" data-testid="admin-blog-ai-topic" />
          </Field>
          <Field label="Angle (optional)">
            <input type="text" value={angle} onChange={(e) => setAngle(e.target.value)} className="inp" placeholder="e.g. focus on lead-gen for new agents" data-testid="admin-blog-ai-angle" />
          </Field>
          <Field label="Target keywords (comma-separated, optional)">
            <input type="text" value={keywords} onChange={(e) => setKeywords(e.target.value)} className="inp" placeholder="real estate Instagram automation, property DM bot" data-testid="admin-blog-ai-keywords" />
          </Field>
          <Field label="Word target">
            <input type="number" min="500" max="3000" step="100" value={wordTarget} onChange={(e) => setWordTarget(parseInt(e.target.value || "1000", 10))} className="inp" data-testid="admin-blog-ai-word-target" />
          </Field>
        </div>
        <div className="mt-6 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <button type="button" onClick={onClose} disabled={generating} className="btn-secondary justify-center">Cancel</button>
          <button type="button" onClick={generate} disabled={generating} className="btn-primary justify-center" data-testid="admin-blog-ai-submit">
            {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating (~60s)…</> : <><Sparkles className="w-4 h-4" /> Generate draft</>}
          </button>
        </div>
      </div>
    </div>
  );
}
